import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FleetPage, FleetSection, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { getPreferences, isOffline, putPreferences } from '../../../../api-new/state/notify'
import { CATEGORIES, CHANNELS, categoryHelp, categoryLabel, channelLabel } from '../../../../shared/notify/words'
import { useSelfNotifyAccess } from '../../../../shared/notify/self-access'
import { PushOptIn } from './push/push-opt-in'
import { INBOX_PATH } from './notification-bell'
import './notifications.scss'

const OFFLINE = "We can't reach notifications right now. Check your connection, then try again."
const LOAD_FAILED = "Your notification preferences couldn't be loaded."
const SAVE_FAILED = "Your preferences couldn't be saved. Please try again."

const keyOf = (category, channel) => `${category}:${channel}`
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

// { 'learning:email': true, ... } from a C-4 PreferenceMatrix.
const valuesOf = matrix => {
  const v = {}
  ;((matrix && matrix.cells) || []).forEach(c => (v[keyOf(c.category, c.channel)] = Boolean(c.enabled)))
  return v
}

// The cells that differ, as PreferenceUpdate cells. Security is never sent:
// it is locked on (C-4: disabling it is 400 notify.security_locked).
export const changedCells = (before, after) =>
  Object.keys(after)
    .filter(k => after[k] !== before[k])
    .map(k => {
      const [category, channel] = k.split(':')
      return { category, channel, enabled: after[k] }
    })
    .filter(c => c.category !== 'security')

const quietOf = matrix => {
  const q = matrix && matrix.quietHours
  return q ? { on: true, start: q.start || '', end: q.end || '', timezone: q.timezone || '' } : { on: false, start: '22:00', end: '07:00', timezone: viewerZone() }
}

const viewerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch (e) {
    return ''
  }
}

const sameQuiet = (a, b) => a.on === b.on && (!a.on || (a.start === b.start && a.end === b.end && a.timezone === b.timezone))

// The person's notification preferences in the active organization (C-4
// /preference): a category x channel matrix resolved login+org -> login ->
// organization default -> platform default. Security is always on and
// locked. A save writes this organization's rows, or with "Every
// organization I belong to" the login-wide rows. Quiet hours hold push
// (never security or critical) between two times in a time zone. Web push
// for this browser is switched on under the matrix (push/push-opt-in).
export const PreferencesPage = () => {
  const access = useSelfNotifyAccess()
  const [matrix, setMatrix] = useState(undefined) // undefined loading, null failed
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [values, setValues] = useState({})
  const [quiet, setQuiet] = useState(quietOf(null))
  const [scope, setScope] = useState('organization')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, saveFailure, setSaveError] = useErrorLine()
  const [reload, setReload] = useState(0)
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  useEffect(() => {
    setMatrix(undefined)
    getPreferences().then(
      m => {
        if (!live.current) return
        setMatrix(m || { cells: [], channels: [] })
        setValues(valuesOf(m))
        setQuiet(quietOf(m))
        setLoadError(null)
      },
      err => {
        if (!live.current) return
        setMatrix(null)
        setLoadError(isOffline(err) ? OFFLINE : serverMessage(err, LOAD_FAILED), isOffline(err) ? null : err)
      }
    )
  }, [reload, setLoadError])

  const stored = useMemo(() => valuesOf(matrix), [matrix])
  const storedQuiet = useMemo(() => quietOf(matrix), [matrix])
  const channels = useMemo(() => {
    const offered = (matrix && matrix.channels) || []
    return CHANNELS.filter(c => offered.includes(c))
  }, [matrix])
  const categories = useMemo(() => {
    const present = new Set(((matrix && matrix.cells) || []).map(c => c.category))
    return CATEGORIES.filter(c => present.has(c))
  }, [matrix])
  const lockedOf = useMemo(() => {
    const m = {}
    ;((matrix && matrix.cells) || []).forEach(c => (m[keyOf(c.category, c.channel)] = Boolean(c.locked) || c.category === 'security'))
    return m
  }, [matrix])

  const cells = changedCells(stored, values)
  const quietChanged = !sameQuiet(quiet, storedQuiet)
  // notify's own rule: 00:00 to 23:59.
  const quietInvalid = quiet.on && (!HHMM.test(quiet.start) || !HHMM.test(quiet.end) || !quiet.timezone.trim())
  const dirty = cells.length > 0 || quietChanged
  const canSave = access.canWrite && dirty && !saving && !quietInvalid

  const save = async e => {
    e.preventDefault()
    if (!canSave) return
    const body = { scope, cells }
    if (quietChanged) body.quietHours = quiet.on ? { start: quiet.start, end: quiet.end, timezone: quiet.timezone.trim() } : null
    setSaving(true)
    setSaved(false)
    setSaveError(null)
    try {
      const next = await putPreferences(body)
      if (!live.current) return
      const settled = next && next.cells ? next : { ...matrix, cells: (matrix.cells || []).map(c => ({ ...c, enabled: values[keyOf(c.category, c.channel)] })) }
      setMatrix(settled)
      setValues(valuesOf(settled))
      setQuiet(quietOf(settled))
      setSaved(true)
    } catch (err) {
      if (live.current) setSaveError(isOffline(err) ? OFFLINE : serverMessage(err, SAVE_FAILED), isOffline(err) ? null : err)
    } finally {
      if (live.current) setSaving(false)
    }
  }

  const toggle = (category, channel) => {
    const k = keyOf(category, channel)
    if (lockedOf[k] || !access.canWrite) return
    setSaved(false)
    setValues(v => ({ ...v, [k]: !v[k] }))
  }

  return (
    <FleetPage
      title="Notification preferences"
      purpose="Choose how EducationXR tells you about things in this organization."
      testId="notify-preferences"
      actions={<Link className="btn btn-sm sw-btn" to={INBOX_PATH}>Back to notifications</Link>}>
      {access.reason && <Notice tone="warn" role="status">{access.reason}</Notice>}

      {matrix === undefined && <p className="sw-hint" role="status">Loading…</p>}
      {matrix === null &&
        <Notice tone={loadError === OFFLINE ? 'warn' : 'bad'} role="alert">
          <p className="mb-2">{loadError}</p>
          <ErrorReference error={loadFailure} className="mb-2" />
          <button type="button" className="btn btn-sm sw-btn" onClick={() => setReload(n => n + 1)}>Try again</button>
        </Notice>}

      {matrix &&
        <form onSubmit={save}>
          <FleetSection id="nt-matrix" title="What to tell you, and how" lede="Security notifications are always on, so you always hear about new sign-ins.">
            <div className="table-responsive">
              <table className="table nt-matrix" aria-label="Notification preferences">
                <thead>
                  <tr>
                    <th scope="col"><span className="visually-hidden">Category</span></th>
                    {channels.map(ch => <th key={ch} scope="col" className="text-center">{channelLabel(ch)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat =>
                    <tr key={cat} aria-label={categoryLabel(cat)}>
                      <th scope="row">
                        {categoryLabel(cat)}
                        {cat === 'security' && <span className="sw-hint fw-normal ms-2">Always on</span>}
                        {categoryHelp(cat) && <div className="sw-hint small fw-normal">{categoryHelp(cat)}</div>}
                      </th>
                      {channels.map(ch => {
                        const k = keyOf(cat, ch)
                        const has = Object.prototype.hasOwnProperty.call(values, k)
                        return (
                          <td key={ch}>
                            {has &&
                              <input type="checkbox" className="form-check-input"
                                aria-label={`${categoryLabel(cat)}: ${channelLabel(ch)}`}
                                checked={lockedOf[k] ? true : Boolean(values[k])}
                                disabled={lockedOf[k] || !access.canWrite}
                                onChange={() => toggle(cat, ch)} />}
                          </td>
                        )
                      })}
                    </tr>)}
                </tbody>
              </table>
            </div>

            <fieldset className="mt-2">
              <legend className="sw-label">Apply these choices to</legend>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="nt-scope" id="nt-scope-org" checked={scope === 'organization'} disabled={!access.canWrite} onChange={() => setScope('organization')} />
                <label className="form-check-label" htmlFor="nt-scope-org">This organization</label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="nt-scope" id="nt-scope-login" checked={scope === 'login'} disabled={!access.canWrite} onChange={() => setScope('login')} />
                <label className="form-check-label" htmlFor="nt-scope-login">Every organization I belong to</label>
              </div>
            </fieldset>
          </FleetSection>

          <FleetSection id="nt-quiet" title="Quiet hours" lede="No push notifications between these times. Security alerts still come through.">
            <div className="form-check mb-2">
              <input className="form-check-input" type="checkbox" id="nt-quiet-on" checked={quiet.on} disabled={!access.canWrite}
                onChange={e => { const on = e.target.checked; setSaved(false); setQuiet(q => ({ ...q, on })) }} />
              <label className="form-check-label" htmlFor="nt-quiet-on">Quiet hours</label>
            </div>
            {quiet.on &&
              <div className="nt-quiet">
                <div className="sw-field">
                  <label className="sw-label" htmlFor="nt-quiet-start">From</label>
                  <input id="nt-quiet-start" type="time" className="form-control form-control-sm" value={quiet.start} disabled={!access.canWrite}
                    onChange={e => { const start = e.target.value; setSaved(false); setQuiet(q => ({ ...q, start })) }} />
                </div>
                <div className="sw-field">
                  <label className="sw-label" htmlFor="nt-quiet-end">Until</label>
                  <input id="nt-quiet-end" type="time" className="form-control form-control-sm" value={quiet.end} disabled={!access.canWrite}
                    onChange={e => { const end = e.target.value; setSaved(false); setQuiet(q => ({ ...q, end })) }} />
                </div>
                <div className="sw-field">
                  <label className="sw-label" htmlFor="nt-quiet-tz">Time zone</label>
                  <input id="nt-quiet-tz" type="text" className="form-control form-control-sm" value={quiet.timezone} placeholder="Europe/Paris" disabled={!access.canWrite}
                    onChange={e => { const timezone = e.target.value; setSaved(false); setQuiet(q => ({ ...q, timezone })) }} />
                </div>
              </div>}
            {quietInvalid && <p className="sw-required small mt-1" role="alert">Enter both times and a time zone.</p>}
          </FleetSection>

          {saveError && <p className="text-danger small" role="alert">{saveError}</p>}
          {saveError && <ErrorReference error={saveFailure} className="mb-2" />}
          {saved && <p className="text-success small" role="status">Saved.</p>}
          <button type="submit" className="btn sw-btn-primary mb-3" disabled={!canSave}>Save</button>
        </form>}

      {matrix && channels.includes('push') && <PushOptIn />}
    </FleetPage>
  )
}
